import {
  Flex,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Text,
} from '@kognitos/lattice';
import type { ISelectGroup, ISelectItem, TRepeatFrequency } from './types';

/**
 * Props for the ScheduleInputDropdown component.
 */
export interface IScheduleInputDropdownProps {
  /** Label text displayed above the input */
  label?: string;
  /** Input type (e.g., "number", "time", "text") */
  inputType?: React.HTMLInputTypeAttribute;
  /** Controlled value for the input */
  inputValue?: string | number;
  /** Placeholder text for the input */
  inputPlaceholder?: string;
  /** Callback when input value changes */
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Additional props to pass to the input element */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Additional className for the input */
  inputClassName?: string;
  /** Aria label for the input for accessibility */
  inputAriaLabel?: string;
  /** Test ID for the input for e2e testing */
  inputTestId?: string;
  /** Controlled value for the select */
  selectValue?: string;
  /** Placeholder text for the select */
  selectPlaceholder?: string;
  /** Callback when select value changes */
  onSelectChange?: (value: string | TRepeatFrequency) => void;
  /** Items to render in the select dropdown (flat list) */
  selectItems?: ISelectItem[];
  /** Grouped items to render in the select dropdown (takes precedence over selectItems) */
  selectGroups?: ISelectGroup[];
  /** Aria label for the select for accessibility */
  selectAriaLabel?: string;
  /** Test ID for the select for e2e testing */
  selectTestId?: string;
  /** Whether the input and select are disabled */
  disabled?: boolean;
}

/** Empty select items array constant to avoid re-creating on each render */
const EMPTY_SELECT_ITEMS: ISelectItem[] = [];

/** Empty select groups array constant to avoid re-creating on each render */
const EMPTY_SELECT_GROUPS: ISelectGroup[] = [];

/**
 * A reusable component that renders a labeled input with an optional adjacent dropdown select.
 * Used for schedule configuration fields like "Every X weeks" or "Time + Timezone".
 * If no select props are provided, only the label and input are rendered.
 *
 * @param props - Component props
 * @returns JSX element containing the input and optional dropdown layout
 */
export function ScheduleInputDropdown({
  label,
  inputType = 'text',
  inputValue,
  inputPlaceholder,
  onInputChange,
  inputProps,
  inputClassName,
  inputAriaLabel,
  inputTestId,
  selectValue,
  selectPlaceholder,
  onSelectChange,
  selectItems = EMPTY_SELECT_ITEMS,
  selectGroups = EMPTY_SELECT_GROUPS,
  selectAriaLabel,
  selectTestId,
  disabled = false,
}: IScheduleInputDropdownProps) {
  const hasGroups = selectGroups.length > 0;
  const showSelect =
    hasGroups ||
    selectItems.length > 0 ||
    onSelectChange !== undefined ||
    selectValue !== undefined;

  /**
   * Renders the select content based on whether groups are provided.
   * Groups take precedence over flat items.
   */
  const renderSelectContent = () => {
    if (hasGroups) {
      return selectGroups.map(group => (
        <SelectGroup key={group.label}>
          <SelectLabel>{group.label}</SelectLabel>
          {group.items.map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      ));
    }

    return selectItems.map(item => (
      <SelectItem key={item.value} value={item.value}>
        {item.label}
      </SelectItem>
    ));
  };

  return (
    <Flex gap={2} align="end" width="full" justify="center">
      <Flex
        direction="column"
        gap={3}
        className={showSelect ? 'flex-1' : 'w-full'}
      >
        {label && (
          <Text level="small" weight="medium" className="px-1">
            {label}
          </Text>
        )}
        <Input
          type={inputType}
          value={inputValue ?? ''}
          placeholder={inputPlaceholder}
          onChange={onInputChange}
          className={`h-9 placeholder:text-muted-foreground text-foreground ${inputClassName ?? ''}`}
          aria-label={inputAriaLabel ?? label}
          data-testid={inputTestId}
          disabled={disabled}
          {...inputProps}
        />
      </Flex>
      {showSelect && (
        <Select
          value={selectValue}
          onValueChange={onSelectChange}
          disabled={disabled}
        >
          <SelectTrigger
            className="flex-1 placeholder:text-muted-foreground text-foreground"
            aria-label={selectAriaLabel}
            data-testid={selectTestId}
          >
            <SelectValue placeholder={selectPlaceholder} />
          </SelectTrigger>
          <SelectContent>{renderSelectContent()}</SelectContent>
        </Select>
      )}
    </Flex>
  );
}
